import { InfiniteScroll } from "../../nq-component";
import BaseListPage from "../../base/BaseListPage.js";
import { getMonthNumber } from "../../monthUtils.js";
import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  upsertUseCase,
} from "../../usecases/object/index.js";
import { getCurrentUserUseCase } from "../../usecases/user/index.js";
import withRouter from "../../withRouter.js";
import MonthsListPagePresenter from "./MonthsListPagePresenter.js";
import MonthsContainer from "../../components/MonthsContainer/index.js";
import CustomLoader from "../../components/CustomLoader/index.js";
import Filters from "./components/Filters/index.js";
import PageHeader from "../../components/PageHeader/index.js";
import SearchFilterCard from "../../components/SearchFilterCard/index.js";
import LegendBox from "../../components/LegendBox/index.js";

class MonthsListPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.state = {
      objects: [],
      selected: [],
      year: new Date().getFullYear(),
      isMonthsVisible: true,
      month: new Date().getMonth() + 1,
    };

    this.presenter = new MonthsListPagePresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
      upsertUseCase(),
      getCurrentUserUseCase(),
    );
  }

  componentDidUpdate(prevProps) {
    if (this.props.params.collection !== prevProps.params.collection) {
      this.presenter.getObjects();
    }
  }

  getCollectionName() {
    return this.props.params.collection;
  }

  getClientId() {
    return this.props.params.id;
  }

  addObject() {
    this.navigateTo("/chart-of-accounts/form");
  }

  getMonths() {
    const currentYear = new Date().getFullYear();
    return this.presenter.getMonths(currentYear);
  }

  onView(month, year) {
    const monthInteger = getMonthNumber(month);
    const collectionName = this.getCollectionName();
    const isAdmin = this.isAdmin();
    const clientId = this.getClientId();

    if (isAdmin) {
      return this.navigateTo(
        `/${collectionName}/${monthInteger}/${year}/${clientId}`,
      );
    }

    return this.navigateTo(`/${collectionName}/${monthInteger}/${year}`);
  }

  onChange(value, field) {
    this.setState(value);
  }

  onFilter() {
    this.presenter.init();
    this.presenter.getObjects();
  }

  onSelect(month, year) {
    this.setState((prevState) => {
      const isAlreadySelected = prevState.selected.some(
        (s) => s.month === month && s.year === year,
      );
      const selected = isAlreadySelected
        ? prevState.selected.filter(
            (s) => !(s.month === month && s.year === year),
          )
        : [...prevState.selected, { month, year }];
      return { ...prevState, selected };
    });
  }

  onReadyToAudit() {
    this.showConfirmDialog(
      "Are you sure you want to mark the selected months as ready to audit?",
      "Ready to Audit",
      "Confirm",
      "Cancel",
    )
      .then(() => {
        this.presenter.onReadyToAudit();
      })
      .catch(() => {});
  }

  render() {
    const schema = this.getSchema(this.getCollectionName());
    if (!schema) return <CustomLoader />;

    const { year, objects, progress, selected } = this.state;
    const user = this.getCurrentUser();

    const months = this.getMonths();
    const yearToNumber = Number(year);

    const collectionName = this.getCollectionName();
    return (
      <>
        <InfiniteScroll className="p-3 p-xl-4 overflow-auto">
          <div className="d-flex flex-column gap-4">
            {/* Header Section */}
            <PageHeader
              breadcrumb={[
                { label: "Home", path: "/" },
                {
                  label: `${collectionName === "sales" ? "Incoming " : "Outgoing "} Transactions`,
                },
              ]}
              title={`${collectionName === "sales" ? "Incoming " : "Outgoing "} Transactions`}
              description={
                collectionName === "sales"
                  ? "View and manage all incoming transactions from your sales records."
                  : "View and manage all outgoing transactions related to expenses and purchases."
              }
              action={() =>
                selected?.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-success fw-medium text-light"
                    onClick={this.onReadyToAudit.bind(this)}
                  >
                    <i className="bi bi-check-circle me-2"></i>
                    Ready to audit
                  </button>
                ) : null
              }
            />

            <div
              className="bg-white rounded-3 shadow-sm"
              style={{
                padding: "1.5rem",
                border: "1px solid #e9ecef",
              }}
            >
              <Filters
                year={yearToNumber}
                onChange={this.onChange.bind(this)}
                onFilter={this.onFilter.bind(this)}
              />
            </div>

            <LegendBox compact className="col-12 col-md-11 col-lg-9" />
            {/* Content Card */}

            {progress ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "400px", padding: "3rem" }}
              >
                <CustomLoader />
              </div>
            ) : (
              <div
                className="rounded-3 shadow-sm border-1 border-primary"
                style={{
                  overflow: "hidden",
                }}
              >
                <MonthsContainer
                  type={collectionName}
                  objects={objects}
                  months={months}
                  year={yearToNumber}
                  onView={this.onView.bind(this)}
                  progress={progress}
                  selected={selected}
                  onSelect={this.onSelect.bind(this)}
                  checkbox={true}
                  isAdmin={this.isAdmin()}
                />
              </div>
            )}
          </div>
        </InfiniteScroll>
      </>
    );
  }
}

export default withRouter(MonthsListPage);
